'use-client';
import * as React from 'react';
import classNames from 'clsx';
import {
  formatClassNames,
  getDataAttributes,
  getTabIndexAttribute,
} from '@wix/editor-elements-common-utils';
import { useAnalyticsReportClicks } from '@wix/editor-elements-common-utils/hooks';
import Link from '@wix/thunderbolt-elements/components/Link';
import type { IImageXProps } from '../ImageX.types';
import { TestIds } from '../constants';

import semanticClassNames from '../ImageX.semanticClassNames';
import { ImageLayer } from './ImageX.imageLayer';

import style from './style/ImageX.scss';
import { getDisplayModeStyle } from './ImageX.utils';

const ImageX: React.FC<IImageXProps> = (props: IImageXProps) => {
  const {
    id,
    skin,
    className,
    customClassNames = [],
    link,
    showLink,
    imageInfo,
    aspectRatio,
    onClick,
    onDblClick,
    onMouseEnter,
    onMouseLeave,
    reportBiOnClick,
    shouldUseResponsiveImages,
    sources,
    defaultSrc,
    getPlaceholder,
    isInFirstFold,
    objectFit,
    a11y,
    hasPlatformClickHandler = false,
  } = props;
  const dataAttributes = getDataAttributes(props);

  const hasLink = !!(link && Object.keys(link).length && showLink);
  if (hasLink) {
    // @ts-expect-error
    link['data-motion-part'] = `BG_LAYER ${id}`;
  }

  const imageLayerProps = {
    id,
    imageInfo,
    defaultSrc,
    getPlaceholder,
    sources,
    isInFirstFold,
    objectFit,
    shouldUseResponsiveImages,
    imageLayerClass: style.imageLayer,
    cancelClip: hasLink,
  };

  const displayModeStyles = getDisplayModeStyle(
    id,
    imageInfo.imageData.displayMode,
    aspectRatio,
  );

  const _onClick = useAnalyticsReportClicks({
    onClick,
    reportBiOnClick,
  });

  const shouldAttachClickHandler =
    hasPlatformClickHandler || hasLink || onClick;

  return (
    <div
      id={id}
      {...dataAttributes}
      {...getTabIndexAttribute(a11y)}
      data-testid={TestIds.root}
      className={classNames(
        style[skin],
        className,
        shouldUseResponsiveImages && style.responsiveImg,
        formatClassNames(semanticClassNames.root, ...customClassNames),
      )}
      onClick={shouldAttachClickHandler ? _onClick : undefined}
      onDoubleClick={onDblClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {displayModeStyles ? (
        <style data-testid={TestIds.displayModeStyle}>
          {displayModeStyles}
        </style>
      ) : null}
      {hasLink ? (
        <Link {...link} className={style.imageStyling}>
          <ImageLayer {...imageLayerProps} className={style.linkedImage} />
        </Link>
      ) : (
        <ImageLayer {...imageLayerProps} className={style.imageStyling} />
      )}
    </div>
  );
};

export default ImageX;
